import { Decimal, toDecimal } from './decimal.js';
import { levelSortOf } from '../constants/levels.js';

/** Minimum EP sell quantity (PWA listing). */
export const EP_TRADE_MIN_ORDER_EP = 6;
/** Maximum EP sell quantity (PWA listing). */
export const EP_TRADE_MAX_ORDER_EP = 1310;
/**
 * Legacy buyer-fee divisor: buyer_fee = amount × (2 × listing_fee) / 131.
 * Must stay 131 for parity with historic settlements — do not reuse as min order.
 */
export const EP_TRADE_FEE_BASE_EP = 131;

/**
 * Buyer fee at 131 EP reference size (meeting 演算):
 *   A3: deduct 17 → get 114
 *   A4: deduct 10 → get 121
 *   A5+: deduct 3 → get 128
 */
export const EP_BUYER_FEE_AT_131: Readonly<Record<'A3' | 'A4' | 'A5', number>> = {
  A3: 17,
  A4: 10,
  A5: 3,
};

export type EpTradeAmounts = {
  seller_lock_ep: string;
  buyer_settle_ep: string;
  buyer_fee_ep: string;
  listing_fee_ep: string;
  buyer_fee_tier?: 'A3' | 'A4' | 'A5' | 'legacy';
};

function roundEpAmount(value: ReturnType<typeof toDecimal>): string {
  return value.toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toFixed(3);
}

/** Resolve buyer fee tier from level_code (below A3 uses A3 rates). */
export function resolveEpBuyerFeeTier(levelCode: string | null | undefined): 'A3' | 'A4' | 'A5' {
  const sort = levelSortOf(levelCode ?? 'E1');
  if (sort >= levelSortOf('A5')) return 'A5';
  if (sort >= levelSortOf('A4')) return 'A4';
  return 'A3';
}

/**
 * Level-based buyer settle: fee = amount × (fee_at_131 / 131).
 */
export function computeEpTradeAmountsForBuyerLevel(
  amountEp: string,
  listingFeeEp: number,
  buyerLevelCode: string | null | undefined,
): EpTradeAmounts {
  const amount = toDecimal(amountEp);
  const listingFee = toDecimal(String(listingFeeEp));
  const tier = resolveEpBuyerFeeTier(buyerLevelCode);
  const feeAt131 = EP_BUYER_FEE_AT_131[tier];
  const buyerFee = amount.mul(feeAt131).div(EP_TRADE_FEE_BASE_EP);
  const buyerSettle = amount.sub(buyerFee);
  return {
    listing_fee_ep: roundEpAmount(listingFee),
    seller_lock_ep: roundEpAmount(amount.add(listingFee)),
    buyer_fee_ep: roundEpAmount(buyerFee),
    buyer_settle_ep: roundEpAmount(buyerSettle),
    buyer_fee_tier: tier,
  };
}

/** Legacy flat formula (≈ A4 when listing_fee=5). */
export function computeEpTradeAmounts(amountEp: string, listingFeeEp: number): EpTradeAmounts {
  const amount = toDecimal(amountEp);
  const listingFee = toDecimal(String(listingFeeEp));
  const sellerLock = amount.add(listingFee);
  const buyerFee = amount.mul(listingFee.mul(2)).div(EP_TRADE_FEE_BASE_EP);
  const buyerSettle = amount.sub(buyerFee);

  return {
    listing_fee_ep: roundEpAmount(listingFee),
    seller_lock_ep: roundEpAmount(sellerLock),
    buyer_fee_ep: roundEpAmount(buyerFee),
    buyer_settle_ep: roundEpAmount(buyerSettle),
    buyer_fee_tier: 'legacy',
  };
}

export function resolveStoredEpTradeAmounts(order: {
  amount_ep: string;
  seller_lock_ep?: string | null;
  buyer_settle_ep?: string | null;
}): EpTradeAmounts {
  const amount = order.amount_ep;
  if (order.seller_lock_ep && order.buyer_settle_ep) {
    const sellerLock = toDecimal(order.seller_lock_ep);
    const buyerSettle = toDecimal(order.buyer_settle_ep);
    const amountDec = toDecimal(amount);
    return {
      listing_fee_ep: roundEpAmount(sellerLock.sub(amountDec)),
      seller_lock_ep: order.seller_lock_ep,
      buyer_settle_ep: order.buyer_settle_ep,
      buyer_fee_ep: roundEpAmount(amountDec.sub(buyerSettle)),
    };
  }
  return computeEpTradeAmounts(amount, 5);
}

/** Fee share slots at 131 EP reference size (meeting 演算). */
export const EP_BUYER_FEE_SLOTS_AT_131: Readonly<
  Record<'A3' | 'A4' | 'A5', Readonly<Partial<Record<'A3' | 'A4' | 'A5', number>>>>
> = {
  A3: { A3: 4, A4: 5, A5: 8 },
  A4: { A4: 4, A5: 6 },
  A5: { A5: 3 },
};

export type EpFeeShareSlot = 'A3' | 'A4' | 'A5';

export type EpUplineFeePayout = {
  user_id: number;
  amount_ep: string;
  slots: EpFeeShareSlot[];
};

/**
 * Split buyer fee along the referrer chain (immediate → root).
 *
 * Rules (131 EP reference; amounts scale with actual fee):
 * - A3 buy (17): A3(4) → A4(5) → ≥A5(8); missing lower slot rolls into next higher
 * - A4 buy (10): A4(4) → ≥A5(6); first ≥A5 takes all remaining
 * - A5 buy (3): first ≥A5 takes 3
 */
export function allocateEpBuyerFeeUpline(params: {
  buyerFeeEp: string;
  buyerFeeTier: 'A3' | 'A4' | 'A5';
  upline: Array<{ user_id: number; level_code: string }>;
}): EpUplineFeePayout[] {
  const fee = toDecimal(params.buyerFeeEp);
  if (fee.lte(0)) return [];

  const feeBase = EP_BUYER_FEE_AT_131[params.buyerFeeTier];
  const bases = EP_BUYER_FEE_SLOTS_AT_131[params.buyerFeeTier];
  const remaining: Record<EpFeeShareSlot, Decimal> = {
    A3: toDecimal(0),
    A4: toDecimal(0),
    A5: toDecimal(0),
  };
  for (const slot of ['A3', 'A4', 'A5'] as const) {
    const base = bases[slot];
    if (base != null && base > 0) {
      remaining[slot] = fee.mul(base).div(feeBase);
    }
  }

  const a3Sort = levelSortOf('A3');
  const a4Sort = levelSortOf('A4');
  const a5Sort = levelSortOf('A5');
  const byUser = new Map<number, { amount: Decimal; slots: EpFeeShareSlot[] }>();

  const takeSlots = (userId: number, slots: EpFeeShareSlot[]) => {
    let amount = toDecimal(0);
    const taken: EpFeeShareSlot[] = [];
    for (const slot of slots) {
      if (remaining[slot].gt(0)) {
        amount = amount.add(remaining[slot]);
        remaining[slot] = toDecimal(0);
        taken.push(slot);
      }
    }
    if (amount.lte(0) || taken.length === 0) return;
    const prev = byUser.get(userId);
    if (prev) {
      prev.amount = prev.amount.add(amount);
      for (const s of taken) {
        if (!prev.slots.includes(s)) prev.slots.push(s);
      }
    } else {
      byUser.set(userId, { amount, slots: taken });
    }
  };

  const remainingTotal = () => remaining.A3.add(remaining.A4).add(remaining.A5);

  for (const node of params.upline) {
    if (remainingTotal().lte(0)) break;
    const sort = levelSortOf(node.level_code ?? 'E1');
    if (sort < 0) continue;

    if (sort >= a5Sort) {
      takeSlots(node.user_id, ['A3', 'A4', 'A5']);
      break;
    }
    if (params.buyerFeeTier === 'A5') continue;

    if (sort >= a4Sort) {
      takeSlots(node.user_id, ['A3', 'A4']);
      continue;
    }
    if (params.buyerFeeTier === 'A3' && sort >= a3Sort) {
      takeSlots(node.user_id, ['A3']);
    }
  }

  const payouts: EpUplineFeePayout[] = [];
  for (const [userId, row] of byUser) {
    payouts.push({
      user_id: userId,
      amount_ep: roundEpAmount(row.amount),
      slots: row.slots,
    });
  }
  return payouts;
}
